import React, { useCallback, useMemo, useState, useRef, useEffect } from 'react'

import checkoutComApi from '@api/checkoutCom'
import config from '@config'
import { PAYMENT_ACTIONS } from '@constants/paymentActions'
import { PAYMENT_PROVIDERS } from '@constants/paymentProviders'
import { PaymentMethodStatus } from '@enums'
import { ConfirmPaymentParams, CheckoutComPaymentResponse, SubmitEventPayload } from '@hooks/useBookingFlow'
import { collectCheckoutComDeviceFingerprint } from '@lib/checkoutComDeviceFingerprint'
import Sentry from '@lib/sentry'
import { PaymentMethod } from '@pages/Checkout/hooks/Payment/useBookingPayment'
import PaymentLabel from '@pages/Checkout/Payment/Label'

interface CheckoutComCard {
  isAvailable(): Promise<boolean>
  isValid(): boolean
  mount(selector: string): void
  submit(): void
  tokenize(): Promise<{ data: { token: string } } | void>
  unmount?(): void
}

const CONTAINER_ID = 'checkoutcom-credit-card-container'
const INTEGRATION_TYPE = 'card'
const brands = [{ type: 'visa' }, { type: 'mc' }, { type: 'me' }]

export const useCheckoutComCreditCard = (): PaymentMethod => {
  const cardRef = useRef<CheckoutComCard | null>(null)
  const [error, setError] = useState<Error | null>(null)
  const [status, setStatus] = useState(PaymentMethodStatus.Pending)
  const [deviceSessionId, setDeviceSessionId] = useState<string | null>(null)

  const init = useCallback(async () => {
    try {
      setError(null)
      setStatus(PaymentMethodStatus.Pending)

      try {
        const deviceSessionId = await collectCheckoutComDeviceFingerprint()
        setDeviceSessionId(deviceSessionId)
      } catch (riskError) {
        /* istanbul ignore next: logging only */
        Sentry.captureException(riskError, {
          tags: {
            component: 'useCheckoutComCreditCard',
            operation: 'collectDeviceFingerprint',
          },
        })
        /* istanbul ignore next: logging only */
        console.warn(
          'Risk SDK initialization or device fingerprint collection failed, continuing without it:',
          riskError,
        )
      }

      const session = await checkoutComApi.createPaymentSession({
        payment_provider: PAYMENT_PROVIDERS.CHECKOUTCOM,
      })

      const checkoutModule = await import('@checkout.com/checkout-web-components')
      const CheckoutWebComponents = checkoutModule.loadCheckoutWebComponents
      const checkout = await CheckoutWebComponents({
        paymentSession: session.provider_response,
        publicKey: config.checkoutcom.publicKey,
        environment: config.checkoutcom.env,
      })

      const flow = checkout.create(INTEGRATION_TYPE, { showPayButton: false })
      cardRef.current = flow
      setStatus(PaymentMethodStatus.Ready)
    } catch (err) {
      console.error('Checkout.com initialization failed:', err)
      const error = new Error('Failed to initialize payment method')
      setError(error)
      setStatus(PaymentMethodStatus.Rejected)
    }
  }, [])

  const mount = useCallback(async () => {
    try {
      const container = document.getElementById(CONTAINER_ID)
      const flow = cardRef.current

      if (!container || !flow) {
        /* istanbul ignore next: cannot test this locally */
        throw new Error('Checkout.com flow is not available')
      }

      const isAvailable = await flow.isAvailable()
      if (!isAvailable) {
        /* istanbul ignore next: cannot test this locally */
        throw new Error('Checkout.com flow is not available')
      }

      flow.mount(`#${CONTAINER_ID}`)
    } catch (err) {
      console.error('Checkout.com mounting failed:', err)
      /* istanbul ignore next: cannot test this locally */
      const error = err instanceof Error ? err : new Error('Failed to mount payment method')
      setError(error)
      setStatus(PaymentMethodStatus.Rejected)
    }
  }, [])

  const mountCardForm = useCallback(
    (container: HTMLDivElement) => {
      if (container) {
        cardRef.current?.unmount?.()
        void mount()
      }
    },
    [mount],
  )

  useEffect(() => {
    void init()

    return () => {
      cardRef.current?.unmount?.()
      cardRef.current = null
    }
  }, [init])

  const getOption = useCallback(() => {
    return {
      value: 'credit_card',
      label: <PaymentLabel type="credit_card" brands={brands} showName />,
      content: (
        <div>
          <div id={CONTAINER_ID} ref={mountCardForm} />
        </div>
      ),
    }
  }, [mountCardForm])

  const confirmPayment = useCallback((response: ConfirmPaymentParams) => {
    const actionData = response.details?.action as CheckoutComPaymentResponse['details']['action']
    if (response.action === PAYMENT_ACTIONS.THREE_D_SECURE && actionData?.redirect) {
      window.location.href = actionData.redirect
    }
  }, [])

  const submitForm = useCallback(async (): Promise<SubmitEventPayload> => {
    try {
      setError(null)
      const result = await cardRef.current?.tokenize()

      if (!result || !result.data) {
        const error = new Error('Tokenization failed - no data returned')
        setError(error)
        throw error
      }

      const paymentMethodData: { type: string; token: string; device_session_id?: string } = {
        type: 'card',
        token: result.data.token,
      }

      /* istanbul ignore next */
      return {
        paymentMethodData,
        ...(deviceSessionId && { deviceSessionId }),
      }
    } catch (err) {
      console.error('Checkout.com tokenization failed:', err)
      /* istanbul ignore next: cannot test this locally */
      const error = err instanceof Error ? err : new Error('Failed to process payment card')
      setError(error)
      throw error
    }
  }, [deviceSessionId])

  const validate = useCallback(() => {
    const card = cardRef.current
    /* istanbul ignore next: cannot test this locally */
    const isValid = card?.isValid?.() ?? false

    if (!isValid) {
      return { paymentMethodData: 'Invalid card fields' }
    }

    return {}
  }, [])

  const beforeSubmit = useCallback(() => {
    const card = cardRef.current
    if (!card?.isValid?.()) {
      card?.submit()
    }
  }, [])

  return useMemo(
    () => ({
      status,
      getOption,
      provider: PAYMENT_PROVIDERS.CHECKOUTCOM,
      error,
      on: {
        beforeSubmit,
        confirmPayment,
        submitForm,
        validate,
      },
    }),
    [getOption, submitForm, validate, error, confirmPayment, status, beforeSubmit],
  )
}
